import React, { Children, cloneElement, memo, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap/dist/gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import clsx from 'clsx';
import { screens } from 'tailwindcss/defaultTheme';
import { useMediaQuery } from '@react-hook/media-query';
import tw from 'tailwind-styled-components';
import throttle from 'lodash.throttle';

gsap.registerPlugin(ScrollTrigger);

const Container = tw.div`
  relative
  flex
  flex-wrap
  gap-8
  w-full

  md:flex-nowrap
`;

const SideScroller = ({ children, itemWidth = 0.55 }) => {
  const containerRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const isMobile = useMediaQuery(`only screen and (max-width: ${screens.md})`);

  useEffect(() => {
    const container = containerRef.current;
    const containerWidth = container.offsetWidth;

    const recalculateWidth = throttle(() => {
      const containerWidth = container.offsetWidth;
      const childWidth = containerWidth * itemWidth;

      Array.from(container.children).forEach(child => {
        child.style.width = isMobile ? '' : `${childWidth}px`;
      });

      setTimeout(() => {
        ScrollTrigger.refresh();
        ScrollTrigger.update();
      }, 0);
    }, 150);

    recalculateWidth();

    const totalWidth = Array.from(container.children).reduce(
      (acc, child) => acc + child.offsetWidth + 24,
      0
    );

    const animation = !isMobile && gsap.to(container, {
      x: `-${totalWidth - containerWidth}px`,
      scrollTrigger: {
        trigger: container,
        scrub: 1,
        start: 'center center',
        end: () => `+=${totalWidth - containerWidth}`,
        pin: pinned,
        invalidateOnRefresh: true,
      }
    });

    window.addEventListener('resize', recalculateWidth);

    return () => {
      window.removeEventListener('resize', recalculateWidth);

      if (animation) {
        animation.reverse();
        animation.scrollTrigger.kill();
      }

      Array.from(container.children).forEach(child => {
        child.style.width = '';
      });
    };
  }, [itemWidth, pinned, isMobile]);

  useEffect(() => {
    setPinned(true);
  }, []);

  return (
    <Container ref={containerRef}>
      {Children.map(children, child => {
        return cloneElement(child, {
          className: clsx(child.props.className, 'flex-shrink-0 max-w-full', { 'w-full': isMobile })
        });
      })}
    </Container>
  );
};

export default memo(SideScroller);
